<template>
  <ShiciGameLayout>
    <!-- 游戏控制区域 -->
    <div class="control-section">
      <el-row :gutter="20">
        <el-col :xs="24" :sm="12" :md="8">
          <el-button 
            type="primary" 
            @click="startGame" 
            :loading="isLoading"
            class="start-btn"
          >
            {{ isLoading ? '加载中...' : '开始游戏' }}
          </el-button>
        </el-col>
        <el-col :xs="24" :sm="24" :md="8" class="score-col">
          <span class="score-text">当前得分: {{ score }}</span>
        </el-col>
      </el-row>
    </div>

    <!-- 游戏区域 -->
    <div v-if="gameStarted" class="game-section">
      <!-- 题目显示 -->
      <div class="question-card">
        <div class="question-header">
          <h2 class="question-title">第 {{ currentQuestionIndex + 1 }} 题 / 共 {{ questions.length }} 题</h2>
          <span class="question-type">{{ questionTypeText }}</span>
        </div>
        <div class="question-content">
          <p class="question-text">{{ displayQuestionText }}</p>
          <p v-if="currentQuestion.poem && (currentQuestion.poem.title || currentQuestion.poem.author)" class="question-ref">
            ——《{{ currentQuestion.poem.title }}》 {{ currentQuestion.poem.author }}
          </p>
        </div>
        <div class="options-section">
          <el-button 
            v-for="(option, index) in currentQuestion.options" 
            :key="index"
            :type="selectedOption === index ? 'primary' : 'default'"
            @click="selectOption(index)"
            :disabled="showFeedback"
            class="option-btn"
          >
            {{ option }}
          </el-button>
        </div>
        <div class="question-footer">
          <el-button 
            @click="submitAnswer" 
            :disabled="showFeedback || selectedOption === -1 || isSubmitting"
            :loading="isSubmitting"
            type="primary"
            class="submit-btn"
          >
            {{ isSubmitting ? '提交中...' : '提交答案' }}
          </el-button>
        </div>
      </div>

      <!-- 答案反馈 -->
      <div v-if="showFeedback" class="feedback-card">
        <div :class="['feedback-header', isCorrect ? 'correct' : 'incorrect']">
          <h3>{{ isCorrect ? '回答正确！' : '回答错误' }}</h3>
        </div>
        <div class="feedback-content">
          <p v-if="!isCorrect" class="correct-answer">正确答案: {{ feedbackCorrectAnswer }}</p>
          <p class="explanation">{{ feedbackExplanation }}</p>
          <div class="poem-info">
            <div class="poem-header-row">
              <h4 class="poem-title">{{ currentQuestion.poem.title }}</h4>
              <div class="poem-actions">
                <el-tag v-if="isCurrentFavorite" type="success" size="mini">已收藏</el-tag>
                <el-button
                  size="mini"
                  :type="isCurrentFavorite ? 'success' : 'default'"
                  :loading="isTogglingFavorite"
                  @click="toggleFavorite"
                >
                  {{ isCurrentFavorite ? '取消收藏' : '收藏' }}
                </el-button>
              </div>
            </div>
            <p class="author">{{ currentQuestion.poem.author }}</p>
            <div class="poem-split">
              <div class="poem-left">
                <div class="poem-content">
                  <p
                    v-for="(line, idx) in poemLinesForFeedback"
                    :key="idx"
                    :class="['poem-line', line.isHighlight ? 'poem-line-highlight' : '']"
                  >
                    {{ line.text }}
                  </p>
                </div>
              </div>
              <div class="poem-right">
                <div class="annotation-title">注释</div>
                <div class="annotation-text">{{ currentAnnotationText }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="feedback-footer">
          <el-button 
            @click="nextQuestion" 
            type="primary"
            class="next-btn"
          >
            {{ currentQuestionIndex < questions.length - 1 ? '下一题' : '结束游戏' }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 游戏结束区域 -->
    <div v-if="gameEnded" class="game-over-section">
      <div class="game-over-card">
        <h2 class="game-over-title">游戏结束</h2>
        <div class="game-over-content">
          <p class="final-score">最终得分: {{ score }} / {{ questions.length }}</p>
          <p class="accuracy">{{ accuracy }}% 正确率</p>
          <div class="stats">
            <span>正确: {{ correctCount }} 题</span>
            <span>错误: {{ incorrectCount }} 题</span>
          </div>
        </div>
        <div class="game-over-footer">
          <el-button 
            @click="restartGame" 
            type="primary"
            class="restart-btn"
          >
            重新开始
          </el-button>
        </div>
      </div>
    </div>

    <!-- 抓取诗词弹窗 -->
    <el-dialog
      title="从网络抓取诗词"
      :visible.sync="showFetchDialog"
      width="400px"
      center
    >
      <div class="fetch-shici-form">
        <el-form :model="fetchForm" label-width="80px">
          <el-form-item label="数量">
            <el-input-number 
              v-model="fetchForm.count" 
              :min="1" 
              :max="10" 
              :step="1"
            ></el-input-number>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="showFetchDialog = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="fetchShici" 
          :loading="isFetching"
        >
          {{ isFetching ? '抓取中...' : '抓取' }}
        </el-button>
      </div>
    </el-dialog>
  </ShiciGameLayout>
</template>

<script>
import axios from 'axios';
import { UseUrl } from "../../utils/config";
import ShiciGameLayout from "./ShiciGameLayout.vue";

export default {
  name: "shicigame",
  components: {
    ShiciGameLayout
  },
  data() {
    return {
      // 游戏状态
      me: {},
      gameStarted: false,
      gameEnded: false,
      isLoading: false,
      isSubmitting: false,
      showFeedback: false,
      isCorrect: false,
      feedbackExplanation: "",
      
      // 题目数据
      questions: [],
      currentQuestionIndex: 0,
      selectedOption: -1,
      feedbackCorrectAnswer: "",
      highlightLine: "",
      isTogglingFavorite: false,
      
      // 得分
      score: 0,
      correctCount: 0,
      incorrectCount: 0,
      
      // 抓取诗词表单
      showFetchDialog: false,
      fetchForm: {
        count: 5
      },
      isFetching: false
    }
  },
  mounted() {
    this.refreshMe()
    window.addEventListener('shicigame-auth-changed', this.refreshMe, false)
  },
  beforeDestroy() {
    window.removeEventListener('shicigame-auth-changed', this.refreshMe, false)
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentQuestionIndex] || {};
    },
    questionTypeText() {
      if (!this.currentQuestion.type) return "";
      if (this.currentQuestion.type === "fill_blank") return "填空题";
      if (this.currentQuestion.type === "next_line") return "补下句";
      if (this.currentQuestion.type === "prev_line") return "补上句";
      return "题目";
    },
    displayQuestionText() {
      const q = this.currentQuestion || {};
      if (!q.type) return "";
      if (q.type === "next_line") {
        const placeholderLen = this.getNextLinePlaceholderLen(q);
        const placeholder = "＿".repeat(placeholderLen);
        return `上句：${q.question || ""}\n下句：${placeholder}`;
      }
      if (q.type === "prev_line") {
        const placeholderLen = this.getPrevLinePlaceholderLen(q);
        const placeholder = "＿".repeat(placeholderLen);
        return `上句：${placeholder}\n下句：${q.question || ""}`;
      }
      return q.question || "";
    },
    accuracy() {
      if (this.questions.length === 0) return 0;
      return Math.round((this.correctCount / this.questions.length) * 100);
    },
    poemLinesForFeedback() {
      const content = this.currentQuestion?.poem?.content;
      if (!content) return [];
      const highlight = (this.highlightLine || "").trim();
      return this.splitPoemToLines(content).map((text) => {
        const isHighlight = highlight ? this.normalizeText(text).includes(this.normalizeText(highlight)) : false;
        return { text, isHighlight };
      });
    },
    isCurrentFavorite() {
      return !!this.currentQuestion?.poem?.favorite;
    },
    currentAnnotationText() {
      const t = String(this.currentQuestion?.poem?.annotation || "").trim();
      return t ? t : "暂无注释";
    }
  },
  methods: {
    refreshMe() {
      try {
        this.me = JSON.parse(localStorage.getItem('shicigame_user') || '{}') || {}
      } catch (e) {
        this.me = {}
      }
    },
    startGame() {
      this.isLoading = true;
      const params = {
        type: "mixed",
        difficulty: "medium",
        count: 10
      };
      
      axios.post(UseUrl + "/shicigame/getgame", params).then((response) => {
        const questions = response?.data?.questions;
        if (!Array.isArray(questions) || questions.length === 0) {
          this.$message.warning("题库为空，请先抓取或添加诗词");
          this.questions = [];
          this.gameStarted = false;
          this.gameEnded = false;
          this.isLoading = false;
          return;
        }
        this.questions = questions;
        this.gameStarted = true;
        this.gameEnded = false;
        this.currentQuestionIndex = 0;
        this.selectedOption = -1;
        this.score = 0;
        this.correctCount = 0;
        this.incorrectCount = 0;
        this.showFeedback = false;
        this.feedbackCorrectAnswer = "";
        this.highlightLine = "";
        this.isLoading = false;
      }).catch((error) => {
        console.error("获取题目失败:", error);
        const serverMessage = error?.response?.data?.message || error?.response?.data?.error;
        const status = error?.response?.status;
        const statusText = status ? `(${status})` : "";
        this.$message.error(serverMessage ? `获取题目失败${statusText}: ${serverMessage}` : `获取题目失败${statusText}，请稍后重试`);
        this.questions = [];
        this.gameStarted = false;
        this.gameEnded = false;
        this.isLoading = false;
      });
    },
    
    selectOption(index) {
      this.selectedOption = index;
    },
    
    submitAnswer() {
      if (this.selectedOption === -1) return;
      
      this.isSubmitting = true;
      const selectedAnswer = this.currentQuestion.options[this.selectedOption];
      
      const params = {
        questionId: this.currentQuestion.id,
        answer: selectedAnswer
      };
      
      axios.post(UseUrl + "/shicigame/checkanswer", params).then((response) => {
        this.isCorrect = response.data.correct;
        this.feedbackExplanation = response.data.explanation;
        this.feedbackCorrectAnswer = response.data.correctAnswer || this.currentQuestion.answer || "";
        this.highlightLine = this.getHighlightLineForFeedback(this.currentQuestion, this.feedbackCorrectAnswer);
        
        if (this.isCorrect) {
          this.score++;
          this.correctCount++;
        } else {
          this.incorrectCount++;
        }
        
        this.showFeedback = true;
        this.isSubmitting = false;
      }).catch((error) => {
        console.error("检查答案失败:", error);
        this.$message.error("检查答案失败，请稍后重试");
        this.isSubmitting = false;
      });
    },
    toggleFavorite() {
      const poem = this.currentQuestion?.poem;
      if (!poem) return;
      this.isTogglingFavorite = true;
      const params = {
        id: poem.id || "",
        title: poem.title || "",
        author: poem.author || ""
      };
      axios.post(UseUrl + "/shicigame/favorite", params).then((response) => {
        const ok = !!response?.data?.success;
        if (!ok) {
          this.$message.error(response?.data?.message || "收藏失败");
          this.isTogglingFavorite = false;
          return;
        }
        const fav = !!response?.data?.favorite;
        const q = this.questions[this.currentQuestionIndex];
        if (q && q.poem) {
          this.$set(q.poem, "favorite", fav);
        }
        this.$message.success(fav ? "已收藏" : "已取消收藏");
        this.isTogglingFavorite = false;
      }).catch(() => {
        this.$message.error("收藏失败，请稍后重试");
        this.isTogglingFavorite = false;
      });
    },
    
    nextQuestion() {
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
        this.selectedOption = -1;
        this.showFeedback = false;
        this.feedbackCorrectAnswer = "";
        this.highlightLine = "";
        this.isTogglingFavorite = false;
      } else {
        this.gameEnded = true;
        this.gameStarted = false;
      }
    },
    
    restartGame() {
      this.startGame();
    },
    
    fetchShici() {
      this.isFetching = true;
      const params = {
        count: this.fetchForm.count
      };
      
      axios.post(UseUrl + "/shicigame/fetch", params).then((response) => {
        if (response.data.success) {
          this.$message.success(`成功添加 ${response.data.added} 首诗词，跳过 ${response.data.skipped} 首已存在的诗词`);
          this.showFetchDialog = false;
        } else {
          this.$message.error(response.data.message);
        }
        this.isFetching = false;
      }).catch((error) => {
        console.error("抓取诗词失败:", error);
        this.$message.error("抓取诗词失败，请稍后重试");
        this.isFetching = false;
      });
    },
    
    formatContent(content) {
      if (!content) return [];
      return content.split(/[\n\r]/).filter(line => line.trim() !== '');
    },
    splitPoemToLines(content) {
      const s = String(content || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      const delims = new Set(["\n", "。", "！", "？", "；", ".", "!", "?", ";"]);
      const lines = [];
      let buf = "";
      for (const ch of Array.from(s)) {
        buf += ch;
        if (delims.has(ch)) {
          const t = buf.trim();
          if (t) lines.push(t);
          buf = "";
        }
      }
      const tail = buf.trim();
      if (tail) lines.push(tail);
      return lines;
    },
    normalizeText(s) {
      return String(s || "").replace(/\s+/g, "").trim();
    },
    getHighlightLineForFeedback(question, correctAnswer) {
      if (!question) return "";
      if (question.type === "fill_blank") {
        const ca = String(correctAnswer || "").trim();
        if (!ca) return "";
        return String(question.question || "").replace(/＿+/g, ca);
      }
      return String(correctAnswer || "").trim();
    },
    getNextLinePlaceholderLen(question) {
      const options = (question && question.options) || [];
      const lens = options.map((x) => this.runeLen(x));
      const maxLen = lens.length ? Math.max(...lens) : 10;
      return Math.max(6, Math.min(24, maxLen));
    },
    getPrevLinePlaceholderLen(question) {
      const options = (question && question.options) || [];
      const lens = options.map((x) => this.runeLen(x));
      const maxLen = lens.length ? Math.max(...lens) : 10;
      return Math.max(6, Math.min(24, maxLen));
    },
    runeLen(s) {
      return Array.from(String(s || "")).length;
    }
  }
}
</script>

<style scoped>
/* 整体容器 */
.shicigame-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
}

/* 顶部标题区域 */
.header-section {
  position: relative;
  text-align: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  margin-bottom: 30px;
  color: white;
}

.login-user {
  position: absolute;
  right: 14px;
  top: 12px;
  font-size: 12px;
  font-weight: 500;
  opacity: 0.95;
}

.page-title {
  font-size: 42px;
  font-weight: 600;
  margin: 0 0 10px 0;
  letter-spacing: 8px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.page-subtitle {
  font-size: 16px;
  margin: 0;
  opacity: 0.9;
  letter-spacing: 2px;
}

/* 控制区域 */
.control-section {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
}

.start-btn,
.add-btn {
  width: 100%;
}

.score-col {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.score-text {
  font-size: 18px;
  font-weight: 600;
  color: #667eea;
}

/* 游戏区域 */
.game-section {
  margin-bottom: 30px;
}

/* 题目卡片 */
.question-card {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.question-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.question-type {
  background: #667eea;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
}

.question-content {
  margin-bottom: 30px;
}

.question-text {
  font-size: 24px;
  line-height: 1.8;
  text-align: center;
  color: #333;
  margin: 0;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  white-space: pre-wrap;
  font-weight: 600;
  letter-spacing: 1px;
}

.question-ref {
  margin: 12px 0 0 0;
  text-align: center;
  font-size: 14px;
  color: #909399;
}

/* 选项区域 */
.options-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 30px;
}

.option-btn {
  padding: 15px;
  font-size: 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
  white-space: normal;
  height: auto;
  line-height: 1.4;
  text-align: left;
  word-break: break-word;
}

.option-btn.el-button {
  white-space: normal;
}

.option-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.question-footer {
  text-align: center;
}

.submit-btn {
  min-width: 150px;
  padding: 12px 24px;
  font-size: 16px;
  border-radius: 25px;
}

/* 反馈卡片 */
.feedback-card {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-top: 20px;
}

.feedback-header {
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
}

.feedback-header.correct {
  background: #f0f9eb;
  color: #67c23a;
}

.feedback-header.incorrect {
  background: #fef0f0;
  color: #f56c6c;
}

.feedback-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.correct-answer {
  font-size: 16px;
  color: #f56c6c;
  margin-bottom: 15px;
  white-space: normal;
  word-break: break-word;
}

.explanation {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
}

/* 诗词信息 */
.poem-info {
  background: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.poem-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 5px;
}

.poem-title {
  flex: 1;
  text-align: left;
  margin: 0;
}

.poem-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.poem-info h4 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
  text-align: center;
}

.poem-info .author {
  font-size: 14px;
  color: #666;
  margin: 0 0 15px 0;
  text-align: center;
}

.poem-info .poem-content {
  text-align: center;
}

.poem-split {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.poem-left {
  flex: 1;
  min-width: 0;
}

.poem-right {
  flex: 1;
  min-width: 0;
  text-align: left;
  padding: 12px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
}

.annotation-title {
  font-size: 14px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
}

.annotation-text {
  font-size: 14px;
  line-height: 1.7;
  color: #555;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 280px;
  overflow: auto;
}

@media (max-width: 768px) {
  .poem-split {
    flex-direction: column;
  }
}

.poem-info .poem-content p {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin: 5px 0;
}

.poem-line-highlight {
  font-weight: 800;
  color: #d32f2f;
  background: #fff1f0;
  border-radius: 6px;
  padding: 4px 8px;
  display: inline-block;
}

.feedback-footer {
  text-align: center;
}

.next-btn {
  min-width: 150px;
  padding: 12px 24px;
  font-size: 16px;
  border-radius: 25px;
}

/* 游戏结束区域 */
.game-over-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.game-over-card {
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  text-align: center;
  max-width: 500px;
  width: 100%;
}

.game-over-title {
  font-size: 32px;
  font-weight: 600;
  color: #333;
  margin: 0 0 30px 0;
}

.final-score {
  font-size: 24px;
  font-weight: 600;
  color: #667eea;
  margin: 0 0 10px 0;
}

.accuracy {
  font-size: 18px;
  color: #666;
  margin: 0 0 30px 0;
}

.stats {
  display: flex;
  justify-content: space-around;
  margin-bottom: 30px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
}

.stats span {
  font-size: 16px;
  color: #333;
}

.restart-btn {
  min-width: 150px;
  padding: 12px 24px;
  font-size: 16px;
  border-radius: 25px;
}

/* 表单样式 */
.add-shici-form,
.fetch-shici-form {
  padding: 20px 0;
}


/* 响应式调整 */
@media (max-width: 768px) {
  .shicigame-container {
    padding: 12px;
  }

  .page-title {
    font-size: 28px;
    letter-spacing: 4px;
  }
  
  .header-section {
    padding: 30px 15px;
  }

  .control-section {
    padding: 15px;
  }

  .question-card,
  .feedback-card {
    padding: 18px;
  }

  .question-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .question-title {
    font-size: 18px;
  }
  
  .question-type {
    font-size: 13px;
  }
  
  .question-text {
    font-size: 18px;
    padding: 15px;
    letter-spacing: 0.5px;
  }
  
  .options-section {
    grid-template-columns: 1fr;
  }

  .option-btn {
    padding: 12px;
    font-size: 15px;
  }

  .submit-btn,
  .next-btn,
  .restart-btn {
    width: 100%;
    min-width: 0;
  }

  .poem-info {
    padding: 14px;
  }

  .poem-header-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .poem-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .score-col {
    justify-content: flex-start;
    margin-top: 15px;
  }

  .stats {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
